import React from "react";
import { Dropdown, Menu, Icon, Spin, Tooltip, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import * as R from "ramda";
import styled from "styled-components";
import ConditionalWrap from "conditional-wrap";

import * as gtag from "../../../../lib/gtag";
import { CANDIDATE_LISTS } from "../../../../constants/opportunity";
import {
  moveCandidateToProcess,
  sendScreeningReminder
} from "../../../../store/actions/candidates";
import CardAction from "../../../../components/CandidateCard/CardAction";
import useEmailInvite from "../../../../hooks/custom/useEmailInvite";
import {
  canAdvanceCandidateToProcess,
  getCandidateScreeningStatus,
  getCandidatesListActions,
  getMoveToSelectedBlockReason,
  isRejectedScreeningStatus
} from "../../../../utils/candidates";
import { moveCandidatesToProcessConfirm } from "../../../../utils/batchActions";

const MenuItem = styled(Menu.Item)`
  padding: 8px 48px 8px 18px !important;
  min-height: 30px;
  position: relative;

  &:hover {
    background-color: #fcffe5 !important;
  }
  .ant-tooltip {
    max-width: 300px !important;
  }

  .ant-spin {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
  }
`;

export default function LikeCandidateAction({
  opportunity,
  t,
  candidate,
  list,
  selectedCandidates = [],
  selectedCandidatesLoading = false,
  restrictAdvanceToProcessByScreening,
  updateCandidateStatus
}) {
  const dispatch = useDispatch();
  const [inviteCandidate, inviteCandidateStatus] = useEmailInvite(
    opportunity.idCompany,
    opportunity.id
  );
  const [isDropdownVisible, setIsDropdownVisible] = React.useState(false);

  const sendScreeningReminderStatus = useSelector(
    R.pipe(
      R.path(["candidates", "lists", list]),
      R.find(R.propEq("id", candidate.id)),
      R.propOr("initial", "sendScreeningReminderStatus")
    )
  );

  const {
    showDropdown,
    showMoveToSelected,
    showMoveToProcess,
    showScreeningEmail,
    showInviteToWorcket
  } = getCandidatesListActions(candidate, list, opportunity);

  const moveToSelectedBlockReason = getMoveToSelectedBlockReason({
    candidate,
    selectedCandidates,
    selectedCandidatesLoading
  });

  const blockedMoveToSelectedMessage =
    moveToSelectedBlockReason === "loading"
      ? t("components:CandidateCard.selectedCandidatesLoading")
      : moveToSelectedBlockReason === "process"
      ? t("components:CandidateCard.previousMatch")
      : t("components:CandidateCard.alreadySelected");

  const moveCandidateToSelected = React.useCallback(() => {
    if (moveToSelectedBlockReason) {
      notification.info({
        message: t("global:alert"),
        description: blockedMoveToSelectedMessage
      });
      return;
    }

    updateCandidateStatus({
      candidate,
      status: "accepted",
      list,
      opportunity,
      message: t("global:good"),
      description: t(
        `toasts:candidates.${
          showMoveToProcess ? "movedToProcess" : "movedToSelected"
        }`,
        {
          fullname: R.or(
            R.prop("fullname", candidate),
            `${R.prop("first", candidate)} ${R.prop("last", candidate)}`
          )
        }
      )
    });
  }, [
    updateCandidateStatus,
    candidate,
    moveToSelectedBlockReason,
    blockedMoveToSelectedMessage,
    list,
    opportunity,
    t,
    showMoveToProcess
  ]);

  const moveToProcess = React.useCallback(() => {
    dispatch(
      moveCandidateToProcess({
        candidate,
        list,
        opportunity,
        message: t("global:good"),
        description: t("toasts:candidates.movedToProcess", {
          fullname: R.or(
            R.prop("fullname", candidate),
            `${R.prop("first", candidate)} ${R.prop("last", candidate)}`
          )
        })
      })
    );
  }, [candidate, dispatch, list, opportunity, t]);

  const handleMoveToProcess = React.useCallback(() => {
    if (
      restrictAdvanceToProcessByScreening &&
      !canAdvanceCandidateToProcess(candidate, opportunity)
    ) {
      notification.warning({
        message: t("global:alert"),
        description: t("toasts:candidates.advanceToProcessScreeningBlocked")
      });
      return;
    }

    const screeningStatus = getCandidateScreeningStatus(candidate, opportunity);
    const needsConfirmation =
      [CANDIDATE_LISTS.selected, CANDIDATE_LISTS.suggested].includes(list) &&
      (screeningStatus === "pending" ||
        isRejectedScreeningStatus(screeningStatus));

    if (!needsConfirmation) return moveToProcess();

    return moveCandidatesToProcessConfirm({
      plurals: false,
      pluralsPending: false,
      pluralsRejected: false,
      pendingCount: screeningStatus === "pending" ? 1 : 0,
      rejectedCount: isRejectedScreeningStatus(screeningStatus) ? 1 : 0,
      selectedCount: 1,
      onOk: moveToProcess
    });
  }, [
    candidate,
    list,
    moveToProcess,
    opportunity,
    restrictAdvanceToProcessByScreening,
    t
  ]);

  const handleSelectMenuOption = React.useCallback(
    ({ key, domEvent }) => {
      domEvent.stopPropagation();

      gtag.event({
        category: "VacancyCandidates",
        action: `${list}.${
          candidate.isRegistered ? "Registered" : "External"
        }.${key}`,
        label: opportunity.id
      });

      if (key === "send-screening-email") {
        dispatch(sendScreeningReminder(candidate.id));
      } else if (key === "invite-to-worcket") {
        inviteCandidate(candidate.id);
      } else if (key === "move-to-process") {
        setIsDropdownVisible(false);
        handleMoveToProcess();
      }
    },
    [
      candidate.id,
      candidate.isRegistered,
      dispatch,
      handleMoveToProcess,
      inviteCandidate,
      list,
      opportunity.id
    ]
  );

  if (!showMoveToSelected && !showMoveToProcess) return null;

  const buttonProps = showDropdown
    ? {
        icon: "plus-circle",
        tooltipText: t("components:CandidateCard.actions"),
        dataTest: "show-dropdown-btn"
      }
    : showMoveToProcess
    ? {
        icon: "swap",
        tooltipText: t("components:CandidateCard.moveToProcess"),
        dataTest: "move-to-process-btn",
        onClick: handleMoveToProcess
      }
    : {
        icon: "check-circle",
        tooltipText: moveToSelectedBlockReason
          ? blockedMoveToSelectedMessage
          : t("components:CandidateCard.moveToSelected"),
        dataTest: "move-to-selected-btn",
        iconColor: moveToSelectedBlockReason ? "#bfbfbf" : undefined,
        hoverColor: moveToSelectedBlockReason ? "#bfbfbf" : undefined,
        disabled: moveToSelectedBlockReason === "loading",
        onClick: moveCandidateToSelected
      };

  return (
    <ConditionalWrap
      key="like-candidate-action"
      condition={showDropdown}
      wrap={children => (
        <Dropdown
          trigger={["click"]}
          visible={isDropdownVisible}
          onVisibleChange={visible => {
            setIsDropdownVisible(visible);
          }}
          overlay={
            <Menu onClick={handleSelectMenuOption}>
              {showScreeningEmail && (
                <MenuItem
                  key="send-screening-email"
                  data-test="send-screening-email-btn"
                  disabled={
                    sendScreeningReminderStatus === "loading" ||
                    sendScreeningReminderStatus === "resolved"
                  }
                >
                  <Icon type="mail" />
                  {sendScreeningReminderStatus === "resolved"
                    ? t(
                        "components:LikeCandidateAction.sendScreeningReminderResolved"
                      )
                    : t("components:LikeCandidateAction.sendScreeningReminder")}
                  {sendScreeningReminderStatus === "loading" && (
                    <Spin size="small" />
                  )}
                  <span></span>
                </MenuItem>
              )}
              {showInviteToWorcket && (
                <MenuItem
                  disabled={
                    candidate.inviteStatus === "loading" ||
                    candidate.inviteStatus === "resolved" ||
                    inviteCandidateStatus === "loading" ||
                    inviteCandidateStatus === "resolved"
                  }
                  key="invite-to-worcket"
                  data-test="invite-to-worcket-btn"
                >
                  <Icon type="mail" />
                  <span>
                    {candidate.inviteStatus === "resolved" ||
                    inviteCandidateStatus === "resolved"
                      ? t(
                          "components:LikeCandidateAction.inviteCandidateResolved"
                        )
                      : t("components:LikeCandidateAction.inviteCandidate")}
                  </span>
                  {candidate.inviteStatus === "loading" ||
                    (inviteCandidateStatus === "loading" && (
                      <Spin size="small" />
                    ))}
                </MenuItem>
              )}
              {showMoveToProcess && (
                <MenuItem key="move-to-process" data-test="move-to-process-btn">
                  <Icon type="swap" />
                  <span>
                    {t("components:LikeCandidateAction.moveToProcess")}
                  </span>
                </MenuItem>
              )}
            </Menu>
          }
        >
          {children}
        </Dropdown>
      )}
    >
      <Tooltip
        trigger={["hover", "focus"]}
        placement="right"
        title={<span>{buttonProps.tooltipText}</span>}
      >
        <CardAction
          color="success"
          iconColor={buttonProps.iconColor || "#747c84"}
          hoverColor={buttonProps.hoverColor || "#3d64ff"}
          icon={buttonProps.icon}
          data-test={buttonProps.dataTest}
          disabled={buttonProps.disabled}
          onClick={buttonProps.onClick}
        />
      </Tooltip>
    </ConditionalWrap>
  );
}
